import { isOurApp } from './platform'

/**
 * Android's own Google account picker.
 *
 * A sheet over the app, not a browser: the Custom Tab flow works but visibly
 * leaves the app, which is what was asked about. This is the only way to get
 * the native sheet — it is an Android API and no web code can summon it.
 *
 * An earlier attempt at this crashed the app outright and had to be pulled.
 * Everything here is written on the assumption that it can fail on some
 * handset for reasons nothing in this file can see.
 *
 * What it must never do again is fail *quietly*. The first working version
 * showed the sheet, got nothing back, and fell straight through to the browser
 * — so picking an account produced a second Google account list, in a second
 * window, with no explanation. Every outcome below is now named, and the
 * caller decides what each one deserves.
 */

/**
 * The **web** client id — deliberately, on Android.
 *
 * `requestIdToken` names the *audience* of the token it asks for: the server
 * that will verify it. So this is the web client id the backend already
 * allows, not the Android one. The Android client id is never mentioned in any
 * code; it exists so Play services can check the calling package and signing
 * certificate, and Google looks it up by itself.
 */
export const GOOGLE_WEB_CLIENT_ID =
  '1018587300192-m0n93uesm6v33bahs57tatg52v3lurah.apps.googleusercontent.com'

export type NativeSignIn =
  /** An account was chosen and Google issued a token for it. */
  | { status: 'signed-in'; idToken: string }
  /** The sheet was dismissed on purpose. Offer nothing; they said no. */
  | { status: 'cancelled' }
  /** The picker cannot run on this device or build. The browser still can. */
  | { status: 'unavailable'; reason: string }
  /** It ran, an account was chosen, and no token came back. A misconfiguration. */
  | { status: 'no-token'; reason: string }

/** GoogleSignInStatusCodes, the few that mean something different to a person. */
const CANCELLED = '12501'
const STATUS_MEANING: Record<string, string> = {
  '7': 'no network connection',
  '10': 'this build is not registered for Google sign-in '
    + '(the signing certificate or package name does not match)',
  '12500': 'Google Play services could not complete the sign-in',
  '12502': 'a sign-in is already in progress',
}

/**
 * On by default now, with a way out.
 *
 * It started opt-in because the first attempt crashed the app on the one
 * action it existed for, and a native crash cannot be caught from JavaScript —
 * by the time the native side dies there is nothing left to fall back to. It
 * has since been watched working on a device, so the default is on.
 *
 * The escape hatch stays, inverted: ?nativeauth=0 disables it, ?nativeauth=1
 * restores the default. If some handset does
 * crash on the picker, that is the way back without waiting for a release.
 */
export const NATIVE_PICKER_OFF = 'nativeauth-off'

export function nativePickerTurnedOff(): boolean {
  try {
    const asked = new URLSearchParams(window.location.search).get('nativeauth')
    if (asked === '0') window.localStorage.setItem(NATIVE_PICKER_OFF, '1')
    if (asked === '1') window.localStorage.removeItem(NATIVE_PICKER_OFF)
    return window.localStorage.getItem(NATIVE_PICKER_OFF) === '1'
  } catch {
    return false
  }
}

export function canSignInNatively(): boolean {
  return isOurApp() && !nativePickerTurnedOff()
}

/**
 * Drop whatever account Play services is holding, before asking for one.
 *
 * This is the difference between the two things that both get called "auto
 * login", and conflating them is what made the last attempt useless.
 *
 * **Reopening the app must not ask anything.** The session is restored from
 * localStorage and nothing here runs at all — no Google call, no login screen.
 * That is the auto-login that is wanted.
 *
 * **Tapping "Continue with Google" must always ask.** Play services caches the
 * last account, so `signIn()` resolves silently with it and no sheet appears.
 * Choose the wrong account once — a personal address the server will not
 * accept — and every retry silently re-submits that same rejected account.
 * There is no way out of it from inside the app: not signed in, so no logout
 * to clear it, and the button that should offer a choice offers none.
 *
 * Clearing at sign-out, which is what this used to do, cannot fix that case.
 * A rejected sign-in never reaches sign-out. So it clears here, on the way in,
 * every single time.
 *
 * Bounded: the picker must not be held up by a local cache operation.
 */
async function dropCachedAccount(GoogleAuth: any): Promise<void> {
  if (!GoogleAuth?.signOut) return

  await Promise.race([
    Promise.resolve(GoogleAuth.signOut()).catch(() => {
      /* Nothing cached, which is exactly the state being asked for. */
    }),
    new Promise<void>(resolve => setTimeout(resolve, 1500)),
  ])
}

/** A rejected native call, in words, keeping the status code if there is one. */
function describe(problem: unknown): string {
  const fault = problem as { message?: string; code?: string | number } | null
  const code = fault?.code === undefined || fault.code === null
    ? '' : String(fault.code)
  const meaning = STATUS_MEANING[code]

  if (meaning) return `${meaning} (code ${code})`
  if (fault?.message) return code ? `${fault.message} (code ${code})` : fault.message
  if (code) return `code ${code}`
  return String(problem)
}

function wasCancelled(problem: unknown): boolean {
  const fault = problem as { message?: string; code?: string | number } | null
  return String(fault?.code) === CANCELLED || /cancel/i.test(fault?.message ?? '')
}

/** Ask Android for an account. Never throws; every ending is a status. */
export async function signInWithNativePicker(): Promise<NativeSignIn> {
  if (!canSignInNatively()) {
    return { status: 'unavailable', reason: 'the native picker is switched off' }
  }

  let GoogleAuth: any
  try {
    GoogleAuth = (await import('@codetrix-studio/capacitor-google-auth'))?.GoogleAuth
  } catch (problem) {
    const reason = `the plugin did not load: ${describe(problem)}`
    return { status: 'unavailable', reason }
  }

  if (!GoogleAuth?.signIn) {
    const reason = 'the plugin loaded without a signIn method'
    return { status: 'unavailable', reason }
  }

  try {
    /**
     * The client id has to be passed here. It cannot come from the config.
     *
     * capacitor.config.ts sets `serverClientId`, which is what the plugin's
     * README documents — but the Android source reads `androidClientId`, then
     * `clientId`, then falls back to the string resource `server_client_id`,
     * and never looks at `serverClientId` at all. With none of those set, the
     * fallback won: the plugin ships
     *
     *     <string name="server_client_id">Your Web Client Key</string>
     *
     * as a placeholder, so Android asked Google for a token audienced to the
     * literal text "Your Web Client Key". The sheet still opened, the account
     * still resolved, and `getIdToken()` came back null — which read exactly
     * like the picker not working, and dropped the user into the browser flow
     * for a second account list.
     *
     * Passing it from here wins over all of that (`call.getData()` is checked
     * before the config), which also means the fix needed no new APK.
     * android/…/res/values/strings.xml now overrides the placeholder too, so a
     * future build is correct even without this argument.
     */
    await GoogleAuth.initialize({ clientId: GOOGLE_WEB_CLIENT_ID })
  } catch (problem) {
    // signIn() would throw a null-pointer straight after this: the client is
    // only built inside initialize(), and the plugin's load() is empty.
    const reason = `set-up failed: ${describe(problem)}`
    return { status: 'unavailable', reason }
  }

  // Always, not just after a sign-out. See dropCachedAccount.
  await dropCachedAccount(GoogleAuth)

  let account: any
  try {
    account = await GoogleAuth.signIn()
  } catch (problem) {
    if (wasCancelled(problem)) {
      return { status: 'cancelled' }
    }
    const reason = describe(problem)
    return { status: 'unavailable', reason }
  }

  // The plugin puts it in both places; take either.
  const idToken = account?.authentication?.idToken || account?.idToken
  if (!idToken) {
    const reason = 'Google returned an account but no ID token'
    return { status: 'no-token', reason }
  }

  return { status: 'signed-in', idToken }
}
